package ai.jessie.web.settings

import ai.jessie.shared.settings.OrganisationSettingsResponse
import ai.jessie.shared.settings.SaveOrganisationSettingsRequest
import ai.jessie.shared.settings.SettingsPreviewResponse
import ai.jessie.shared.settings.SettingsProblemResponse
import ai.jessie.web.members.deniesAccess
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.OnboardingRequestFailure
import ai.jessie.web.onboarding.problem
import ai.jessie.web.onboarding.unavailableProblem
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch

data class SettingsUiState(
    val current: OrganisationSettingsResponse? = null,
    val draft: SettingsDraft? = null,
    val preview: SettingsPreviewResponse? = null,
    val pending: SaveOrganisationSettingsRequest? = null,
    val conflicts: List<SettingsConflict> = emptyList(),
    val busy: Boolean = false,
    val error: OnboardingProblem? = null,
    val problems: Map<String, String> = emptyMap(),
    val notice: String? = null,
)

data class SettingsActions(
    val refresh: () -> Unit,
    val edit: () -> Unit,
    val change: ((SettingsDraft) -> SettingsDraft) -> Unit,
    val preview: () -> Unit,
    val confirm: () -> Unit,
    val cancel: () -> Unit,
    val revise: () -> Unit,
)

class SettingsState(
    private val port: SettingsPort,
    private val scope: CoroutineScope,
    private val organisationId: String,
) {
    var state by mutableStateOf(SettingsUiState())
        private set

    val actions =
        SettingsActions(
            refresh = ::refresh,
            edit = {
                if (!state.busy && state.current?.editable == true)
                    state = state.copy(draft = state.current!!.draft(), notice = null)
            },
            change = { update ->
                val draft = state.draft
                if (!state.busy && draft != null && state.preview == null)
                    state = state.copy(draft = update(draft))
            },
            preview = ::preview,
            confirm = ::confirm,
            cancel = {
                if (!state.busy)
                    state =
                        state.copy(
                            draft = null,
                            preview = null,
                            pending = null,
                            conflicts = emptyList(),
                            error = null,
                            problems = emptyMap(),
                            notice = "Abgebrochen. Es wurde nichts gespeichert.",
                        )
            },
            revise = { if (!state.busy) state = state.copy(preview = null, pending = null) },
        )

    fun refresh() {
        if (state.draft != null) return
        request {
            val current = port.read(organisationId)
            currentCoroutineContext().ensureActive()
            state = state.copy(current = current)
        }
    }

    private fun preview() {
        val current = state.current?.takeIf { it.editable && !state.busy }
        val draft = state.draft
        if (current == null || draft == null) return
        val invalid = buildMap {
            listOf("nextInvoiceNumber", "paymentTermsDays").forEach {
                if (draft.fields[it]?.toIntOrNull() == null)
                    put(it, "Bitte eine ganze Zahl eingeben.")
            }
            if (draft.fields["automaticDocumentExtraction"] !in listOf("true", "false"))
                put("automaticDocumentExtraction", "Bitte eine Auswahl treffen.")
        }
        if (invalid.isNotEmpty()) {
            state = state.copy(problems = invalid, error = validationProblem(invalid))
            return
        }
        val proposal = draft.request(current.revision)
        request {
            val preview = port.preview(organisationId, proposal)
            currentCoroutineContext().ensureActive()
            state = state.copy(preview = preview, pending = proposal)
        }
    }

    private fun confirm() {
        val proposal = state.pending ?: return
        if (state.preview == null || state.current?.editable != true) return
        request {
            val saved = port.save(organisationId, proposal)
            currentCoroutineContext().ensureActive()
            state =
                SettingsUiState(
                    current = saved,
                    busy = true,
                    notice =
                        "Einstellungen gespeichert. Neue Vorgänge verwenden die gültigen Einstellungen.",
                )
        }
    }

    private fun request(block: suspend () -> Unit) {
        if (state.busy) return
        state = state.copy(busy = true, error = null, problems = emptyMap(), notice = null)
        scope.launch {
            try {
                block()
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: SettingsRequestFailure) {
                currentCoroutineContext().ensureActive()
                rejected(failure)
            } catch (_: Exception) {
                currentCoroutineContext().ensureActive()
                // Only an unanswered save leaves the stored state unknown; reads changed nothing.
                val error = if (state.pending != null) uncertainProblem() else unavailableProblem()
                state = state.copy(preview = null, pending = null, error = error)
            } finally {
                currentCoroutineContext().ensureActive()
                state = state.copy(busy = false)
            }
        }
    }

    private fun rejected(failure: SettingsRequestFailure) {
        val detail = failure.detail
        val common = OnboardingRequestFailure(failure.status, detail.code, detail.fields)
        when {
            common.deniesAccess() -> state = SettingsUiState(busy = true, error = common.problem())
            detail.code == "STALE_SETTINGS" -> stale(detail)
            detail.code in listOf("INVALID_SETTINGS", "TIMER_OPEN") -> {
                val problems = detail.problems.associate { it.field to it.correction() }
                state =
                    state.copy(
                        preview = null,
                        pending = null,
                        problems = problems,
                        error = validationProblem(problems),
                    )
            }
            else -> state = state.copy(preview = null, pending = null, error = common.problem())
        }
    }

    private fun stale(detail: SettingsProblemResponse) {
        val current = detail.current
        val baseline = state.current
        val intended = state.draft
        // A newer view that is absent or not the owner's means access was lost (A7).
        if (current?.editable != true || baseline == null || intended == null) {
            state =
                SettingsUiState(
                    busy = true,
                    error = OnboardingProblem("Nicht verfügbar oder du hast keinen Zugriff."),
                )
            return
        }
        state =
            state.copy(
                current = current,
                draft = current.draft(),
                preview = null,
                pending = null,
                conflicts = settingsConflicts(baseline, current, intended),
                error =
                    OnboardingProblem(
                        "Inzwischen wurden neuere Einstellungen gespeichert. Nichts aus deinem Vorschlag " +
                            "wurde gespeichert. Prüfe den Vergleich und trage gewünschte Änderungen erneut ein."
                    ),
            )
    }
}

private fun validationProblem(problems: Map<String, String>) =
    OnboardingProblem(
        "Bitte korrigiere die markierten Felder. Es wurde nichts gespeichert.",
        problems.keys.map { "settings-$it" },
    )

private fun uncertainProblem() =
    OnboardingProblem(
        "Die Antwort konnte nicht bestätigt werden. Bitte brich die Bearbeitung ab und lade neu, " +
            "um den gespeicherten Stand zu prüfen."
    )
